import React from 'react';
import { AccordionItem, AccordionTrigger, AccordionContent } from './wealthica-accordion';
import { WealthicaPositionsTable } from './WealthicaPositionsTable';
import { formatCurrency } from '@/lib/portfolioCalculations';
import type { AccountWithPositions } from './container-utils';
import Spinner from '@/components/ui/Spinner';

interface InstitutionAccountItemProps {
  account: any;
  index: number;
  accountsWithPositions: AccountWithPositions[];
  isLoadingForDisplay: (accountId: string) => boolean;
  accountHasPositions: (accountId: string) => boolean;
  getAccountBalance: (account: any) => number;
  onPositionClick?: (position: any) => void;
}

export function InstitutionAccountItem({
  account,
  index,
  accountsWithPositions,
  isLoadingForDisplay,
  accountHasPositions,
  getAccountBalance,
  onPositionClick,
}: InstitutionAccountItemProps) {
  return (
    <AccordionItem value={account.id} className="border-0">
      <AccordionTrigger className={`py-3 px-4 hover:no-underline data-[state=open]:bg-gray-50/50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
        <div className="grid items-center w-full gap-4 grid-cols-[minmax(0,1fr)_max-content] sm:grid-cols-[minmax(0,1fr)_160px_160px]">
          {/* Left: Account name and number */}
          <div className="flex flex-col items-start min-w-0">
            <div className="font-medium text-base truncate" title={account.name}>{account.name}</div>
            <div className="text-xs text-muted-foreground mt-0.5 flex items-center">
              <span className="text-gray-500">{account.number ? `(${account.number})` : ''}</span>
            </div>
          </div>

          {/* Middle: Holdings Value (hidden on mobile) */}
          <div className="hidden sm:flex flex-col items-end">
            {isLoadingForDisplay(account.id) ? (
              <div className="w-24 h-5 bg-gray-200 animate-pulse rounded"></div>
            ) : (
              // Show nothing if there are no positions or holdings total <= 0
              (account.holdingsTotal && account.holdingsTotal > 0) ? (
                <>
                  <div className="font-semibold tabular-nums whitespace-nowrap">
                    {formatCurrency(account.holdingsTotal, account.currency || 'CAD', {
                      showDecimals: true,
                      locale: (account.currency || '').toUpperCase() === 'USD' ? 'en-US' : (account.currency || '').toUpperCase() === 'JPY' ? 'ja-JP' : 'en-CA',
                    })}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 text-right">
                    {(accountsWithPositions.find((a) => a.id === account.id)?.positions?.length || 0)} positions
                  </div>
                </>
              ) : null
            )}
          </div>

          {/* Right: Account Balance */}
          <div className="flex flex-col items-end">
            <div className="font-semibold tabular-nums whitespace-nowrap">
              {isLoadingForDisplay(account.id) ? (
                <div className="w-24 h-5 bg-gray-200 animate-pulse rounded"></div>
              ) : (
                formatCurrency(account.balance || 0, account.currency || 'CAD', {
                  showDecimals: true,
                  locale: (account.currency || '').toUpperCase() === 'USD' ? 'en-US' : (account.currency || '').toUpperCase() === 'JPY' ? 'ja-JP' : 'en-CA',
                })
              )}
            </div>
            <div className="text-[10px] uppercase text-muted-foreground mt-0.5">Balance</div>
          </div>
        </div>
      </AccordionTrigger>

      <AccordionContent className={`px-4 py-3 ${index % 2 === 0 ? 'bg-white/80' : 'bg-gray-50/80'}`}>
        {isLoadingForDisplay(account.id) ? (
          <div className="py-6 text-center text-sm text-muted-foreground flex flex-col items-center justify-center">
            <Spinner size="md" className="mb-2 text-muted-foreground" ariaLabel="Loading positions data" />
            <p>Loading positions data...</p>
          </div>
        ) : accountHasPositions(account.id) ? (
          <div className="pt-2">
            <WealthicaPositionsTable
              positions={accountsWithPositions.find((a) => a.id === account.id)?.positions || []}
              accountName={account.name}
              accountCurrency={account.currency}
              useAlternatingRows={true}
              onPositionClick={onPositionClick}
            />
          </div>
        ) : (
          <div className="hidden">
            <p>No positions found for this account.</p>
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

export default InstitutionAccountItem;
